/**
 * PR-PAYZEN-SMARTFORM-CHECKOUT-UI-V1
 *
 * Renders the PayZen Krypton embedded SmartForm using the Krypton JS SDK V4.0.
 *
 * Integration model:
 *   1. Script loaded dynamically from PayZen CDN.
 *   2. window.KR.setFormConfig() called with formToken + publicKey — async, returns Promise.
 *   3. SDK auto-renders into divs with kr-* CSS classes.
 *   4. KR.onSubmit() fires when the customer completes and submits the form.
 *   5. KR.onError() fires on validation or network errors.
 *
 * FINALITY GUARD: SDK onSubmit ≠ payment confirmed.
 * onSubmit → dispatch PAYZEN_FORM_COMPLETED → AWAITING_CONFIRMATION.
 * Webhook/polling is the sole source of finality (ADR-098).
 *
 * SDK reference: PayZen REST API V4 — Krypton V4.0 (payzen.lat).
 * Validate KR API shape against PayZen sandbox before production certification.
 */

import { useEffect, useRef, useState } from 'react';
import type { CheckoutSession } from '@/lib/types';

const KRYPTON_SCRIPT_URL =
  'https://static.payzen.lat/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js';
const KRYPTON_SCRIPT_ID = 'payzen-krypton-sdk';

interface KryptonInstance {
  setFormConfig(config: {
    formToken: string;
    'kr-public-key': string;
    [key: string]: unknown;
  }): Promise<void>;
  onSubmit(callback: (paymentData: unknown) => boolean | void): void;
  onError(callback: (error: { errorCode?: string; clientMessage?: string }) => void): void;
}

declare global {
  interface Window {
    KR?: KryptonInstance;
  }
}

interface Props {
  session: CheckoutSession;
  formToken: string;
  formPublicKey: string;
  onCompleted: () => void;
  onFailed: (error: string) => void;
}

type LoadState = 'loading' | 'ready' | 'failed';

export function PayzenSmartForm({ session, formToken, formPublicKey, onCompleted, onFailed }: Props) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const initializedRef = useRef(false);

  const amountFormatted = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: session.currency,
    minimumFractionDigits: 2,
  }).format(parseFloat(session.amount));

  useEffect(() => {
    // Guard: only init once per mount — KR SDK is stateful and re-init causes duplicate callbacks
    if (initializedRef.current) return;
    initializedRef.current = true;

    let cancelled = false;

    const loadAndInit = async () => {
      // Load script once per page — idempotent via script ID check
      if (!document.getElementById(KRYPTON_SCRIPT_ID)) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.id = KRYPTON_SCRIPT_ID;
          script.src = KRYPTON_SCRIPT_URL;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('PayZen Krypton SDK failed to load'));
          document.head.appendChild(script);
        });
      }

      if (cancelled) return;

      const KR = window.KR;
      if (!KR) {
        setLoadState('failed');
        return;
      }

      // setFormConfig is async — waits for SDK internal initialization
      await KR.setFormConfig({
        formToken,
        'kr-public-key': formPublicKey,
        'kr-language': 'es-ES',
      });

      if (cancelled) return;

      // Register outcome callbacks
      KR.onSubmit(() => {
        if (!cancelled) {
          // FINALITY GUARD: form submission acknowledged by PayZen ≠ payment confirmed.
          // Dispatch PAYZEN_FORM_COMPLETED → AWAITING_CONFIRMATION.
          // Webhook/polling delivers actual finality.
          onCompleted();
        }
        return false; // suppress default SDK redirect behaviour
      });

      KR.onError((error) => {
        if (!cancelled) {
          const msg =
            error.clientMessage ??
            'Error al procesar el pago. Por favor intenta de nuevo.';
          onFailed(msg);
        }
      });

      if (!cancelled) setLoadState('ready');
    };

    loadAndInit().catch(() => {
      if (!cancelled) setLoadState('failed');
    });

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadState === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-700 mb-4">
            No se pudo cargar el formulario de pago. Por favor recarga la página.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-brand-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Pagar con tarjeta</h2>
          <p className="text-gray-500 text-sm">
            Total:{' '}
            <span className="font-semibold text-gray-900">{amountFormatted}</span>
          </p>
        </div>

        {loadState === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/*
          PayZen Krypton SDK renders card fields into divs with kr-* CSS classes.
          The SDK replaces the placeholder content once setFormConfig() resolves.
          Required structure per Krypton V4.0 embedded form documentation.
        */}
        <div className="kr-embedded">
          <div className="kr-pan" />
          <div className="kr-expiry" />
          <div className="kr-security-code" />
          <button className="kr-payment-button" />
          <div className="kr-form-error" />
        </div>
      </div>
    </div>
  );
}
